/**
 * The finance ledger — `/v1/admin/ledger`.
 *
 * **Every figure on these screens is the server's.** The journal totals, the running per-account
 * effect on a booking, the trial balance and its delta all arrive computed. Money added up in two
 * places is money that eventually disagrees with itself, and the client is the copy nobody can
 * audit.
 *
 * **There is no hook that posts a journal entry**, because there is no endpoint that accepts one.
 * A correction is a typed event emitted by the service that owns it, with a reason and four-eyes
 * upstream. The two writes here are on the chart of accounts, which is configuration.
 *
 * **A rebuild invalidates the trial balance and nothing else.** The snapshot is derived: rebuilding
 * changes how fresh a cached number is, not any fact, so blowing away the journal reads with it
 * would refetch everything to show the same thing.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type JournalRow = components['schemas']['JournalRow'];
export type LedgerLeg = components['schemas']['LedgerLeg'];
export type TrialBalanceRow = components['schemas']['TrialBalanceRow'];
export type AccountRow = components['schemas']['AccountRow'];
export type BookingLedgerLine = components['schemas']['BookingLedgerLine'];
type JournalPage = components['schemas']['JournalPage'];
type JournalDetail = components['schemas']['JournalDetail'];
type BookingLedger = components['schemas']['BookingLedger'];
type TrialBalance = components['schemas']['TrialBalance'];
type MoneySummary = components['schemas']['MoneySummary'];
type SnapshotRebuilt = components['schemas']['SnapshotRebuilt'];
type PanditStatement = components['schemas']['PanditStatement'];
export type AccountCreate = components['schemas']['AccountCreate'];

export interface JournalQuery {
  txn_type?: string;
  account?: string;
  booking_code?: string;
  pandit_profile_id?: number;
  posted_by?: string;
  since?: string;
  until?: string;
  page?: number;
  page_size?: number;
}

/** Only what the operator set. An empty `account=` would narrow to nothing rather than to all. */
export function journalParams(query: JournalQuery): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  return search.toString();
}

export function useJournal(query: JournalQuery) {
  const params = journalParams(query);
  return useQuery({
    queryKey: keys.ledger.journal(query as Readonly<Record<string, unknown>>),
    queryFn: () => authedClient.request<JournalPage>(`/v1/admin/ledger/transactions?${params}`),
  });
}

export function useJournalDetail(transactionId: number | null) {
  return useQuery({
    queryKey: keys.ledger.one(transactionId ?? 0),
    queryFn: () =>
      authedClient.request<JournalDetail>(`/v1/admin/ledger/transactions/${transactionId}`),
    enabled: transactionId !== null,
  });
}

/**
 * Takes a real id, not a nullable one.
 *
 * Its callers only render once a journal names a booking, so a `null` branch here would be one
 * no input can reach — and a guard nobody can exercise is a claim nobody can check. The nullable
 * shape belongs on `useJournalDetail`, where the id comes from the URL and may genuinely be junk.
 */
export function useBookingLedger(bookingId: number) {
  return useQuery({
    queryKey: keys.ledger.booking(bookingId),
    queryFn: () => authedClient.request<BookingLedger>(`/v1/admin/bookings/${bookingId}/ledger`),
  });
}

export function useTrialBalance(month: string) {
  return useQuery({
    queryKey: keys.ledger.trialBalance(month),
    queryFn: () =>
      authedClient.request<TrialBalance>(`/v1/admin/ledger/trial-balance?month=${month}`),
  });
}

/**
 * The dashboard's two money tiles for a month (R303): captured and platform revenue, summed from
 * the journal server-side, so the landing page does not pull the whole trial balance for two
 * numbers. The ledger is the money truth; analytics never feeds these.
 */
export function useMoneySummary(month: string) {
  return useQuery({
    queryKey: keys.ledger.moneySummary(month),
    queryFn: () => authedClient.request<MoneySummary>(`/v1/admin/ledger/money-summary?month=${month}`),
  });
}

export function useRebuildTrialBalance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (month: string) =>
      authedClient.request<SnapshotRebuilt>(
        `/v1/admin/ledger/trial-balance/rebuild?month=${month}`,
        { method: 'POST' },
      ),
    onSuccess: (_result, month) =>
      queryClient.invalidateQueries({ queryKey: keys.ledger.trialBalance(month) }),
  });
}

export function useChartOfAccounts() {
  return useQuery({
    queryKey: keys.ledger.accounts(),
    queryFn: () => authedClient.request<AccountRow[]>('/v1/admin/ledger/accounts'),
  });
}

/** Add-only: a code, once used, is never reused, and the server refuses a clash with `account_exists`. */
export function useAddAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountCreate) =>
      authedClient.request<AccountRow>('/v1/admin/ledger/accounts', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.ledger.accounts() }),
  });
}

/**
 * Description and the active flag — the only two things about an account that may ever change.
 * The reason is a query parameter because the body is the change and the reason is about it.
 */
export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      reason,
      ...body
    }: {
      id: number;
      reason: string;
      description?: string;
      is_active?: boolean;
    }) =>
      authedClient.request<AccountRow>(
        `/v1/admin/ledger/accounts/${id}?${new URLSearchParams({ reason })}`,
        { method: 'PATCH', body },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.ledger.accounts() }),
  });
}

/**
 * The journal as CSV for the accountant, one row per leg. A mutation because every export is
 * audited — it is an act, not a read to cache — and the body is `text/csv`, so it is read as text.
 */
export function useExportJournal() {
  return useMutation({
    mutationFn: ({ since, until }: { since: string; until: string }) =>
      authedClient.request<string>(
        `/v1/admin/ledger/export?${new URLSearchParams({ since, until })}`,
        { parse: 'text' },
      ),
  });
}

/** Same reasoning as `useBookingLedger`: a statement is only asked for once a leg names a pandit. */
export function usePanditStatement(panditProfileId: number, fy: string) {
  return useQuery({
    queryKey: keys.ledger.statement(panditProfileId, fy),
    queryFn: () =>
      authedClient.request<PanditStatement>(
        `/v1/admin/ledger/statements/${panditProfileId}?fy=${fy}`,
      ),
  });
}
